/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { produce } from 'immer';
import type { SessionData, IMvpOffersData } from './types';
import { StateStorage } from 'zustand/middleware';
import { getSessionStoreData, updateServerSession } from './session-utils';
import { createAppStore } from '@marriott/mi-store-utils';
import { canUseDOM, getEnvProp } from '../helpers';
import { logger } from '@marriott/shared/mi-helper-utils';
import { getMvpOffersData } from '../mvpOffers/mvpOffersUtils';
import { isEmpty } from '@marriott/shared/mi-helper-utils';

export type UserSessionActions = {
  updateSession: (data: SessionData, shouldMerge?: boolean) => void;
  setSyncedWithServer: (synced: boolean) => void;
  setIsPrequalComplete: (synced: boolean) => void;
  updateMvpOffersData: (data?: IMvpOffersData) => void;
};

export type UserSessionState = {
  // stored user session attributes
  session: SessionData;
  mvpOffers?: IMvpOffersData;
  syncedWithServer: boolean;
  isPrequalComplete: boolean;
};

export type SessionStoreStr = {
  state: UserSessionState;
  version: number;
};

let serverData: SessionData | null = null;
// fallback when server LastUpdated is not available
const largestTimeStamp = 999999999999999;

const deepMerge = (target: any, source: any) => {
  Object.keys(source)?.forEach(key => {
    if (typeof source[key] === 'object' && source[key] !== null && !Array.isArray(source[key])) {
      target[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      target[key] = source[key]; // overwrite or add new key
    }
  });
  return target;
};

// adding iniital state from server data for sdk disabled products
// this is just a fallback in case any component uses session from useSession on webSDK disabled page
const getInitialSessionState = (): SessionData => {
  if (canUseDOM) {
    const isWebSDKEnabled =
      getEnvProp('NEXT_PUBLIC_ENABLE_WEBSDK') === 'true' &&
      String(window.__NEXT_DATA__?.props?.['pageProps']?.model?.enableWebSDK) === 'true';

    if (!isWebSDKEnabled) {
      const serverSessionData = window?.sessionData?.data ? window?.sessionData : window?.sessionData?.cacheData;

      if (serverSessionData && !isEmpty(serverSessionData)) {
        const { accessToken, ...rest } = serverSessionData.data;
        return { data: rest };
      }
    }
  }

  return {}; // Fallback for SSR or missing sessionData
};
const initialState: UserSessionState = {
  session: getInitialSessionState(),
  syncedWithServer: false,
  isPrequalComplete: false,
  mvpOffers: undefined,
};

export const serverSyncLocalStorage: StateStorage = {
  setItem(name, value) {
    const valueObj = JSON.parse(value) as SessionStoreStr;
    const lastUpdatedNumber = Number(serverData?.data?.lastUpdated);
    const shouldMerge = valueObj?.state?.session?.shouldMerge;
    // if server data timestamp is behind the session store timestamp, push to server
    if (
      (lastUpdatedNumber < Number(valueObj?.state?.session?.data?.lastUpdated) &&
        lastUpdatedNumber !== largestTimeStamp &&
        valueObj?.state?.syncedWithServer === false) ||
      shouldMerge
    ) {
      // push to server
      updateServerSession({
        createOrUpdate: {
          ...valueObj.state.session.data,
        },
        name: 'SyncServerPush',
      });
    }

    // remove shouldMerge from session object
    delete valueObj?.state?.session?.shouldMerge;

    window.localStorage.setItem(name, JSON.stringify(valueObj));
  },
  getItem(name) {
    const sessionStr: SessionStoreStr = JSON.parse(
      window.localStorage.getItem(name) ||
        JSON.stringify({
          version: 0,
          state: {
            ...initialState,
          },
        })
    ) as SessionStoreStr;

    // set to false on initial load
    return JSON.stringify({
      ...sessionStr,
      state: {
        ...sessionStr.state,
        syncedWithServer: false,
        isPrequalComplete: false,
      },
    });
  },
  removeItem(name) {
    window.localStorage.removeItem(name);
  },
};

// @ts-ignore - error TS2347: Untyped function calls may not accept type arguments.
export const useUserSessionStore = createAppStore<UserSessionState & UserSessionActions>(
  // @ts-ignore - error TS7006: Parameter 'set' implicitly has an 'any' type.
  set => ({
    ...{ initialState },
    setSyncedWithServer: (synced: boolean) => {
      set(
        (state: UserSessionState) =>
          produce(state, (draft: UserSessionState) => {
            draft.syncedWithServer = synced;
          }),
        false,
        // @ts-ignore - provide a action name for devtools
        'SessionServerSync'
      );
    },
    setIsPrequalComplete: (synced: boolean) => {
      set(
        (state: UserSessionState) =>
          produce(state, (draft: UserSessionState) => {
            draft.isPrequalComplete = synced;
          }),
        false,
        // @ts-ignore - provide a action name for devtools
        'isPrequalCompleteFlag'
      );
    },
    updateMvpOffersData: (data?: IMvpOffersData) => {
      set(
        (state: UserSessionState) =>
          produce(state, (draft: UserSessionState) => {
            draft.mvpOffers = data;
          }),
        false,
        // @ts-ignore - provide a action name for devtools
        'UpdateMvpOffers'
      );
    },
    updateSession: (data: SessionData, shouldMerge: boolean = false) => {
      set(
        (state: UserSessionState) =>
          produce(state, (draft: UserSessionState) => {
            const { accessToken, ...rest } = data.data || {}; // filtering our accessToken before storing in session store
            const updatedData = {
              ...rest, // everything except accessToken
              lastUpdated:
                data.data?.lastUpdated && data.data?.lastUpdated !== String(largestTimeStamp)
                  ? data.data?.lastUpdated
                  : String(Date.now()),
            };

            if (shouldMerge) {
              // Deep merge into existing session.data - when update session happens from client
              deepMerge(draft, {
                session: {
                  data: updatedData,
                  shouldMerge,
                },
              });
            } else {
              // Completely replace the session object - on iniital load
              draft.session = {
                data: updatedData,
              };
            }
          }),
        false,
        // @ts-ignore - provide an action name for devtools
        'UpdateSession'
      );
    },
  }),
  {
    usePersistentStore: true,
    persistentStoreName: 'mi-session-store',
    devToolsName: 'mi-session-store',
    customStorage: serverSyncLocalStorage,
    onRehydrateStorage: async (state: UserSessionState & UserSessionActions) => {
      const { log } = logger({ requestID: '', sessionID: state?.session?.data?.sessionToken })('UserSessionStore');
      if (
        typeof window === 'undefined' ||
        getEnvProp('NEXT_PUBLIC_ENABLE_WEBSDK') !== 'true' ||
        String(window.__NEXT_DATA__?.props?.['pageProps']?.model?.enableWebSDK) !== 'true'
      ) {
        return;
      }
      const browserDataStr = window.localStorage.getItem('mi-session-store');
      let browserData: SessionStoreStr = {} as SessionStoreStr;
      try {
        browserData = JSON.parse(browserDataStr || '{}') as SessionStoreStr;
      } catch (e) {
        log.warn('Unable to parse session store data', e);
      }
      try {
        const pageProps = window?.__NEXT_DATA__?.props?.['pageProps'];
        const promises = [];
        if (
          getEnvProp('NEXT_PUBLIC_ENABLE_WEBSDK') === 'true' &&
          !pageProps?.SKIP_MVP_OFFERS &&
          getEnvProp('SKIP_MVP_OFFERS') !== 'true'
        ) {
          promises.push(getMvpOffersData(pageProps?.['resolvedUrl'], pageProps?.['currentLocale']));
        }
        if (!serverData) {
          const sesssionAPI = getSessionStoreData()
            .then(res => {
              serverData = res as SessionData;
              if (serverData.data) {
                serverData.data.lastUpdated = (Number(serverData.data?.lastUpdated) || largestTimeStamp).toString();
              }
              const browserTimeStamp = Number(browserData.state?.session?.data?.lastUpdated) || 0;
              const serverTimeStamp = Number(serverData.data?.lastUpdated);
              log.info('Checking timestamps for session update', browserTimeStamp, serverTimeStamp);
              if (browserTimeStamp < serverTimeStamp) {
                state.updateSession({
                  ...serverData,
                  data: {
                    ...serverData.data,
                    lastUpdated: serverTimeStamp !== largestTimeStamp ? String(serverTimeStamp) : String(Date.now()),
                  },
                });
                log.info('Updating session state with server data');
              } else {
                log.info('Client has latest session data, skipping update');
              }
            })
            .catch(err => log.warn('Error fetching session data', err))
            .finally(() => {
              state.setSyncedWithServer(true);
            });
          promises.push(sesssionAPI);
        }
        const result = await Promise.allSettled(promises);
        if (!result?.length) {
          log.error('Session store promise results are empty');
          return;
        }
        const mvpOffersData = promises.length > 1 ? result[0] : null;
        if (mvpOffersData && mvpOffersData.status === 'fulfilled') {
          // @todo - added for backwards compatibility, should be removed when everyone is using the session store
          window.mvpOffers = mvpOffersData.value;
          state.updateMvpOffersData(mvpOffersData.value);
          log.info('MVP Offers updated');
        } else {
          log.warn('MVP Offers failed');
        }
        state.setIsPrequalComplete(true);
      } catch (error) {
        log.error('Error fetching session data', error);
        state.setSyncedWithServer(true);
        state.setIsPrequalComplete(true);
      }
    },
  }
);
